import { ReactNode, useCallback, useState } from 'react'
import {
  forwardRef,
  BoxProps,
  Card,
  PageTabs,
  PageTab,
  IconWithNotification,
  DotIndicator,
  TabProps
} from '~/design_system'

export const IconTab = forwardRef<TabProps, 'button'>(({ children, ...props }, ref) => (
  <PageTab ref={ref} pos="relative" {...props}>
    <IconWithNotification>{children}</IconWithNotification>
  </PageTab>
))

export type IndicatorVal = boolean | string

type IconTabsProps = Omit<BoxProps, 'children'> & {
  children: ({
    indicators,
    setTabIndicator
  }: {
    indicators: IndicatorVal[]
    setTabIndicator: (index: number) => (indVal: IndicatorVal) => void
  }) => ReactNode
  /** The index of the tab to be selected by default */
  defaultTabIndex?: number
}

export const IconTabs = forwardRef<IconTabsProps, 'div'>(({ children, defaultTabIndex = 0, ...props }, ref) => {
  const [indicators, setIndicators] = useState<IndicatorVal[]>([])

  const setTabIndicator = useCallback(
    (i: number) => (indVal: IndicatorVal) => {
      setIndicators(prev => {
        const next = prev.slice(0)
        next[i] = indVal
        return next
      })
    },
    []
  )

  return (
    <Card data-testid="icon-tabs" {...props} ref={ref}>
      <PageTabs defaultIndex={Number(defaultTabIndex)}>{children({ indicators, setTabIndicator })}</PageTabs>
    </Card>
  )
})

export function IconTabIndicator({ indicator }: { indicator: IndicatorVal }) {
  return (
    <>
      {indicator && <DotIndicator />}
      {indicator && typeof indicator === 'string' && <DotIndicator bg={indicator} />}
    </>
  )
}
